import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { APP } from './app.config';
import { GuestHashService } from './guest-hash.service';
import { OrganisationService } from './organisation.service';
import { MyBookingsService } from './my-bookings.service';
import jwt_decode from "jwt-decode";
import { WebAuth } from 'cidaas-javascript-sdk';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface IUserObserver {
  observerId: number;
  onUserChange(user);
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private guestHashService: GuestHashService,
    private organisationService: OrganisationService,
    private router: Router,
    private http: HttpClient,
    private myBookingsService: MyBookingsService
  ) {
    // Initialize Cidaas SDK
    this.initCidaasSDK();

    // Check if user is already logged in
    this.checkAuthentication();
  }
  user;
  private observers: IUserObserver[] = [];
  cidaasAuth: any; // WebAuth instance
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private userInfoSubject = new BehaviorSubject<any>(null);
  private userRolesSubject = new BehaviorSubject<string[]>([]);
  private isLoggingOut = false;
  
  // Token refresh management
  private refreshTokenInProgress = false;
  private refreshTokenSubject = new Subject<string>();

  private initCidaasSDK() {
    // Initialize the Cidaas WebAuth with configuration
    const baseUrl = APP.cidaas.split('/authz-srv')[0]; // Extract base URL from existing config
    const clientId = APP.cidaas.match(/client_id=([^&]*)/)[1]; // Extract client ID from existing config
    
    // Use window.location to get the current URL for the redirect_uri
    const currentUrl = typeof window !== 'undefined' ? window.location.origin : APP.baseurl;
    
    const options = {
      authority: baseUrl,
      client_id: clientId,
      redirect_uri: currentUrl + '/auth/callback',
      scope: 'openid profile email roles offline_access'
    };
    
    // Create WebAuth instance
    this.cidaasAuth = new WebAuth(options);
  }

   // Check if the user is authenticated
  async checkAuthentication(): Promise<boolean> {
    console.log("checkAuthentication()");
    // Don't check authentication if we're in the process of logging out
    if (this.isLoggingOut) {
      console.log("Skipping authentication check during logout");
      return false;
    }
    try {
      const userInfo = await this.cidaasAuth.getUserInfo();
      if (userInfo) {
        this.isAuthenticatedSubject.next(true);
        this.userInfoSubject.next(userInfo);
        this.extractUserRoles(userInfo)
      } else {
        this.isAuthenticatedSubject.next(false);
      }
    } catch (error: any) {
      console.error('Authentication check failed:', error);
      this.isAuthenticatedSubject.next(false);
    }
    console.log("isAuthenticated: " + this.isAuthenticatedSubject.getValue());
    return this.isAuthenticatedSubject.getValue();
  }

  // Extract user roles from user info
  private extractUserRoles(userInfo: any): void {
    console.log('User info received:', userInfo);
    this.userRolesSubject.next(userInfo.profile.roles);
  }

  // Handle login callback
  async handleLoginCallback(): Promise<void> {
    try {
      // Capture old userId before processing login
      const oldUserIdFromStorage = localStorage.getItem('user') 
        ? JSON.parse(localStorage.getItem('user') || '{}').id 
        : null;

      const response = await this.cidaasAuth.loginCallback();
      console.log('Login callback response:', response);
      
      if (response) {
        this.isAuthenticatedSubject.next(true);
        const userInfo = await this.cidaasAuth.getUserInfo();
        console.log('User info from storage:', userInfo);
        
        this.userInfoSubject.next(userInfo);
        this.extractUserRoles(userInfo);

        let token = null;
        token = userInfo.access_token;

        if (token != null) {
          console.log("token is present");
          var decoded = jwt_decode(token);

          const user = {
            // @ts-ignore
            username: decoded.email,
            // @ts-ignore
            token,
          };

          this.saveToLocalStorage(user);
          console.log("logged in save user to ls and notify")
          console.log(user)
          this.notify(user);

          // Handle booking migration for anonymous users
          console.log('Checking for booking migration...');
          console.log('oldUserIdFromStorage:', oldUserIdFromStorage);
          
          if (oldUserIdFromStorage) {
            // @ts-ignore
            const newUserId = decoded.sub;
            console.log('newUserId:', newUserId);
            
            // Only migrate if the userIds are different
            if (oldUserIdFromStorage !== newUserId) {
              try {
                // Get organisation ID from localStorage (saved during app load)
                const organisationId = localStorage.getItem('activeOrganisationId');
                console.log('organisationId from localStorage:', organisationId);
                
                if (!organisationId) {
                  console.warn('No organisation ID available in localStorage for migration');
                } else {
                  console.log(`Attempting to migrate bookings from ${oldUserIdFromStorage} to ${newUserId}`);
                  const result: any = await this.myBookingsService.migrateBookings(
                    oldUserIdFromStorage,
                    newUserId,
                    token,
                    organisationId
                  );
                  
                  console.log(`Successfully migrated ${result?.migratedBookings || 0} bookings to new account`);
                  
                  // Optional: Show success message
                  if (result?.migratedBookings && result.migratedBookings > 0) {
                    console.log(`✓ Your ${result.migratedBookings} existing booking(s) have been linked to your account!`);
                  }
                }
              } catch (migrationError) {
                console.error('Booking migration failed:', migrationError);
                // Don't block login - bookings still accessible via recovery code
                console.log('Note: Bookings are still accessible via recovery code if migration fails');
              }
            } else {
              console.log('User IDs are the same, no migration needed');
            }
          } else {
            console.log('No old user ID found in storage - user is new');
          }
        }
        else {
          console.log("token not present in callback");
        }
      }
        
        this.router.navigate(['/administration']);
      }
    catch (error: any) {
      console.error('Login callback failed:', error);
    }
  }

  getUser(noNotify?) {
    if (localStorage.getItem('user')) {
      try {
        this.user = JSON.parse(localStorage.getItem('user'));
        this.guestHashService.init();
      } catch (e) {
        console.error("Error parsing user from localStorage:", e);
        // Clear invalid user data
        localStorage.removeItem('user');
        this.user = null;
      }
    } else {
      this.guestHashService.init();
      // Don't try to parse user again if it doesn't exist
      this.user = null;
    }
    
    if(noNotify) {
      return this.user;
    }
    
    this.notify(this.user);
    return this.user;
  }

  logout(autoLogout?) {
    return new Promise<void>((resolve) => {
      // Set flag to prevent authentication checks during logout
      this.isLoggingOut = true;
      
      // First clear local state
      this.isAuthenticatedSubject.next(false);
      this.userInfoSubject.next(null);
      this.userRolesSubject.next([]);

      // Use Cidaas SDK for logout
      const currentUrl = typeof window !== 'undefined' ? window.location.origin : APP.baseurl;
      const redirectUri = currentUrl + "/auth/login";
      this.cidaasAuth.logout({
        post_logout_redirect_uri: redirectUri
      }).catch(error => {
        console.error("Logout error:", error);
      });
      
      // Maintain existing behavior for local state cleanup
      if (this.user) {
        this.user.token = null;
        this.user.username = null;
        localStorage.setItem('user', JSON.stringify(this.user));
      } else {
        // Create a new empty user object if none exists
        this.user = { token: null, username: null };
        localStorage.setItem('user', JSON.stringify(this.user));
      }
      
      localStorage.removeItem('lastNav');
      localStorage.removeItem('lastOrga');
      this.organisationService.clearActiveOrganisation();
      
      if(autoLogout) {
        console.log("auto logout")
        this.router.navigate(['/o/', { status: 'You have been automatically logged out for security reasons' }]);
      } else {
        console.log("normal logout")
        // Reset the logout flag after a brief delay to allow navigation to complete
        setTimeout(() => {
          this.isLoggingOut = false;
        }, 500);
        this.router.navigate(['/auth/login']);
      }
      resolve();
    }).then(() => {
      this.notify();
    });
  }

  register(email, password) {
    // Use Cidaas SDK for registration
    // Note: The SDK doesn't have a direct method for registration with just email/password
    // We'll need to use the browser-based registration flow or maintain the existing API call
    return this.http
      .post(APP.baseurl + '/auth/register', {
        username: email,
        password,
      })
      .toPromise();
  }

  login(email, password) {
    // Since the Cidaas SDK loginWithCredentials redirects and doesn't return a Promise,
    // we'll maintain the existing API call for now to keep the current flow
    return this.http
      .post(APP.baseurl + '/auth/login', {
        username: email,
        password,
      })
      .toPromise();
    
    // For reference, this is how we would use the Cidaas SDK directly:
    // this.cidaasAuth.loginWithCredentials({
    //   username: email,
    //   password: password,
    //   username_type: 'email'
    // });
    // But this would redirect the user instead of returning a Promise
  }

   // Method to initiate browser-based login
   loginWithBrowser(returnUrl?: string) {
     // Use window.location to get the current URL for the redirect_uri
     const currentUrl = window.location.origin;
     
     // If a return URL is provided, store it for use after callback
     if (returnUrl) {
       localStorage.setItem('returnUrl', returnUrl);
     }
     
     return this.cidaasAuth.loginWithBrowser({
       redirect_uri: currentUrl + '/auth/callback'
     });
   }
  
  // Method to initiate browser-based registration
  registerWithBrowser() {
    // Use window.location to get the current URL for the redirect_uri
    const currentUrl = window.location.origin;
    return this.cidaasAuth.registerWithBrowser({
      redirect_uri: currentUrl + '/auth/callback'
    });
  }

  subscribe(observer: IUserObserver) {
  
    const idx = this.observers.indexOf(observer);
    if (idx === -1) {
      this.observers.push(observer);
      observer.onUserChange(this.getUser(true));
  
    }
  }

  unsubscribe(observer: IUserObserver) {
    let idx = -1;
    for (const obs of this.observers) {
      if (obs.observerId === observer.observerId) {
        idx = this.observers.indexOf(obs);
      }
    }
    if (idx !== -1) {
      this.observers.splice(idx, 1);
    }
  }

  notify(user?) {

    if(!this.user && !user) {
      this.getUser();
      return;
    }
    
    this.observers.forEach((observer) => {
      observer.onUserChange(user ? user : this.user);
    });
  }

  saveToLocalStorage(user) {
    if (localStorage.getItem('user')) {
      const olduser = JSON.parse(localStorage.getItem('user'));

      olduser.username = user.username;
      olduser.token = user.token;

      localStorage.setItem('user', JSON.stringify(olduser));
    } else {
      localStorage.setItem('user', JSON.stringify(user));
    }
  }

  hasRole(role: string): boolean {
    const roles = this.userRolesSubject.getValue();
    return roles.includes(role);
  }

  isSuperAdmin() {
    return this.hasRole("CREWCUS_SUPERADMIN");
    /* if (!this.user || !this.user.token) {
      console.log("No user or token available");
      return false;
    }
    
    try {
      var decoded: any = jwt_decode(this.user.token);
      
      if (decoded.roles) {
        return decoded.roles.includes('CREWCUS_SUPERADMIN');
      } else {
        console.log("roles missing from token");
        let sub = decoded.sub;
        let userinfo = this.http
          .get(APP.baseurl + '/groupmgmt-srv/users/' + sub)
          .toPromise();
        console.log(userinfo);
        return false;
      }
    } catch (error) {
      console.error("Error decoding token:", error);
      return false;
    } */
  }

  /**
   * Refresh the access token using Cidaas SDK
   * This method is called when a 401 error is received from an API request
   * @returns Promise<string> - New access token if successful
   * @throws Error if refresh fails (will trigger automatic logout)
   */
  async refreshToken(): Promise<string> {
    try {
      // If a refresh is already in progress, wait for it to complete
      if (this.refreshTokenInProgress) {
        console.log('Token refresh already in progress, waiting...');
        return new Promise((resolve, reject) => {
          this.refreshTokenSubject.subscribe({
            next: (newToken) => resolve(newToken),
            error: (err) => reject(err)
          });
        });
      }

      this.refreshTokenInProgress = true;
      console.log('Starting token refresh...');

      // Use Cidaas SDK to refresh the token
      const userInfo = await this.cidaasAuth.getUserInfo();
      
      if (!userInfo || !userInfo.access_token) {
        throw new Error('Failed to refresh token: no access token in userInfo');
      }

      const newToken = userInfo.access_token;
      console.log('Token refreshed successfully');

      // Update the stored token in localStorage
      if (this.user) {
        this.user.token = newToken;
        this.saveToLocalStorage(this.user);
      }

      // Notify subscribers about the new token
      this.refreshTokenSubject.next(newToken);

      return newToken;
    } catch (error: any) {
      console.error('Token refresh failed:', error);
      
      // Clear the in-progress flag
      this.refreshTokenInProgress = false;
      
      // Emit error to subscribers
      this.refreshTokenSubject.error(error);
      
      // If refresh token is expired or invalid, perform logout
      console.log('Performing automatic logout due to token refresh failure');
      this.logout(true);
      
      throw error;
    } finally {
      this.refreshTokenInProgress = false;
    }
  }

  // Observable getters
  get isAuthenticated$(): Observable<boolean> {
    return this.isAuthenticatedSubject.asObservable();
  }

  get userInfo$(): Observable<any> {
    return this.userInfoSubject.asObservable();
  }

  get userRoles$(): Observable<string[]> {
    return this.userRolesSubject.asObservable();
  }

  // Value getters
  get isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.getValue();
  }

  get userInfo(): any {
    return this.userInfoSubject.getValue();
  }

  get userRoles(): string[] {
    return this.userRolesSubject.getValue();
  }
}
